import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FormField } from "@/components/ui/form"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { useSteeringLevelsCapacityQuery$data } from "@/modules/admin/capacity/services/__generated__/useSteeringLevelsCapacityQuery.graphql"
import { useSetSteeringLevelsMutation } from "@/modules/admin/capacity/services/useSetSteeringLevelsMutation"
import { Language, LanguageValues, SteeringLevelValue } from "@/types/__generated__/union-types"

interface SteeringDialogProps {
  steeringLevelData: useSteeringLevelsCapacityQuery$data
  onClose: () => void
}

const SteeringLevelSchema = z.object({
  language: z.custom<Language>(),
  level: z.custom<SteeringLevelValue>(),
  isAutomatic: z.boolean(),
})

const SteeringLevelsSchema = z.object({
  steeringLevels: z.array(SteeringLevelSchema),
})

type SteeringLevelsForm = z.infer<typeof SteeringLevelsSchema>

const SteeringLevelDialog = ({ onClose, steeringLevelData }: SteeringDialogProps) => {
  const { t } = useTranslation()
  const setSteeringLevelMutation = useSetSteeringLevelsMutation()

  const steeringLevels = [...steeringLevelData.steeringLevels]
    .sort((a, b) => LanguageValues.indexOf(a.language) - LanguageValues.indexOf(b.language))
    .map((s) => {
      return {
        language: s.language,
        level: s.level,
        isAutomatic: s.isAutomatic,
        waitingTime: s.waitingTime,
        status: s.waitingTime.status,
      }
    })

  const form = useForm<SteeringLevelsForm>({
    resolver: zodResolver(SteeringLevelsSchema),
    defaultValues: { steeringLevels: steeringLevels },
  })

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = form

  const handleSaveSteeringLevels = (data: SteeringLevelsForm) => {
    if (isDirty) {
      const submitData = data.steeringLevels
      setSteeringLevelMutation(submitData, () => onClose())
    } else onClose()
  }

  const steeringLevelItems: {
    value: SteeringLevelValue
    label: string
  }[] = [
    {
      value: "MINUS_ONE",
      label: "-1",
    },
    {
      value: "ZERO",
      label: "0",
    },
    {
      value: "ONE",
      label: "1",
    },
    {
      value: "TWO",
      label: "2",
    },
    {
      value: "THREE",
      label: "3",
    },
    {
      value: "FOUR",
      label: "4",
    },
  ]

  return (
    <Dialog
      data-testid="steering-level-dialog"
      open
      onOpenChange={() => {
        onClose()
      }}>
      <DialogContentCustom className="min-w-150.5" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("admin.capacity.steeringLevel.dialogHeader")}</DialogTitle>
        </DialogHeader>
        {steeringLevels.map((s, index) => (
          <div key={s.language} className="flex flex-col gap-2 px-6 py-2">
            <label
              className="text-on-surface-subdued text-sm"
              data-testid={`${s.language.toLowerCase()}-label`}>{`${convertLanguageToISOCode(s.language)} (${t(
              "admin.capacity.steeringLevel.waitingTime",
              {
                waitingTime: `${s.waitingTime.status === "EXCEEDS_MAXIMUM" ? "> " : ""}${s.waitingTime.minutesToWait / 60}`,
              },
            )})`}</label>
            <div className="flex items-center gap-4">
              <FormField
                name={`steeringLevels.${index}.isAutomatic`}
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2">
                    <DetailCheckbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid={`steering-level-checkbox-${s.language.toLowerCase()}`}>
                      {t("common.steeringLevel.automatic")}
                    </DetailCheckbox>
                  </label>
                )}
              />
              <FormField
                name={`steeringLevels.${index}.level`}
                control={control}
                render={({ field: { value: val, onChange } }) => (
                  <RadioGroup
                    data-testid={`${s.language.toLowerCase()}-steering-level-radio-group`}
                    className="flex"
                    value={val}
                    onValueChange={onChange}>
                    {steeringLevelItems.map(({ value, label }) => (
                      <RadioGroupItem
                        key={label}
                        data-testid={`steering-level-radio-${s.language.toLowerCase()}-${label.toLocaleLowerCase()}`}
                        className="px-1 py-1.5"
                        value={value}>
                        <RadioGroupLabel>{label}</RadioGroupLabel>
                      </RadioGroupItem>
                    ))}
                  </RadioGroup>
                )}
              />
            </div>
          </div>
        ))}

        <DialogFooter className="flex p-6 pt-4">
          <Button
            className="rounded-[20px] px-6 py-2.5"
            variant="secondary"
            onClick={() => onClose()}
            data-testid="steering-level-dialog-cancel-button">
            {t("common.button.cancel")}
          </Button>
          <Button
            variant="primary"
            className="ml-0 rounded-[20px] px-6 py-2.5"
            data-testid="steering-level-dialog-save-button"
            onClick={handleSubmit(handleSaveSteeringLevels)}>
            {t("common.button.save")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export { SteeringLevelDialog }
